import { type Accessor, createContext, type ParentComponent, useContext } from "solid-js";
import { useSession } from "@/lib/auth";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncSingle } from "@cashsync/shared/solid";
import {
  getUserPreferencesQuery,
  queryKeys,
  type UserPreferences,
  VISITOR_ID,
} from "@cashsync/shared";

interface UserPreferencesContextValue {
  preferences: Accessor<UserPreferences | null>;
  isLoading: Accessor<boolean>;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue>();

export const UserPreferencesProvider: ParentComponent = (props) => {
  const session = useSession();
  const ps = usePowerSyncContext();
  const drizzle = useDrizzle();

  const userQuery = createPowerSyncSingle<UserPreferences>({
    queryKey: () => queryKeys.userPreferences.consolidated(session.userId()),
    db: () => ps.db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const userId = session.userId();
      if (!dbInstance || userId === VISITOR_ID) {
        return null;
      }
      return getUserPreferencesQuery(dbInstance, userId);
    },
    enabled: () => Boolean(drizzle() && session.userId() !== VISITOR_ID),
  });

  const preferences = () => userQuery.data;
  const isLoading = () => userQuery.isLoading;

  return (
    <UserPreferencesContext.Provider value={{ preferences, isLoading }}>
      {props.children}
    </UserPreferencesContext.Provider>
  );
};

const FALLBACK_VALUE: UserPreferencesContextValue = {
  preferences: () => null,
  isLoading: () => false,
};

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    if (typeof __DEV__ !== "undefined" && __DEV__) {
      console.warn(
        "[UserPreferences] useUserPreferences used outside of provider (HMR). Returning fallback.",
      );
      return FALLBACK_VALUE;
    }
    throw new Error("useUserPreferences must be used within a UserPreferencesProvider");
  }
  return context;
}
